import { useState, useRef, useLayoutEffect } from 'react';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Text from '@ui/text/text/text';
import WatermarkTypeSelector from './controls/watermark-type-selector';
import AddWatermarkEmailBlock from './add-watermark-email-block';
import AddWatermarkDateBlock from './add-watermark-date-block';
import AddWatermarkImageBlock from './add-watermark-image-block';
import AddWatermarkCustomBlock from './add-watermark-custom-block';
import { WATERMARK_OPTIONS, DEFAULT_EMAIL_SETTINGS } from '../../../constants/watermark.constants';
import { Case, SwitchCase } from '@ui/switch-case/switch-case';
import { IWatermarkElement, WatermarkElementTypes } from '@/types/watermarks.types';
import { useAppSelector } from '@/store/hooks';
import { selectWatermarksLoading } from '@/store/slices/watermarks/selectors';
import styles from './watermark-settings.module.css';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorsCssMixVariable from '@/utils/colors/get-theme-color-css-mix-variable';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getDefaultSettings = (_type: string): IWatermarkElement['settings'] => {
  return { ...DEFAULT_EMAIL_SETTINGS };
};

interface AddWatermarkElementFormProps {
  element: IWatermarkElement;
  initiallyEditing?: boolean;
  canCancel?: boolean;
  canDelete?: boolean;
  onChange: (el: IWatermarkElement) => void;
  onSave: () => Promise<void>;
  onDelete?: () => void;
  onRemove?: () => void;
}

const AddWatermarkElementForm = ({
  element,
  initiallyEditing = false,
  canCancel = true,
  canDelete = false,
  onChange,
  onSave,
  onDelete,
  onRemove,
}: AddWatermarkElementFormProps) => {
  const isLoading = useAppSelector(selectWatermarksLoading);

  const isNewRef = useRef(initiallyEditing);
  const editStartRef = useRef<IWatermarkElement>(element);

  const [isEditing, setIsEditing] = useState(initiallyEditing);
  const [isDirty, setIsDirty] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isClosing = useRef(false);

  const currentType = WATERMARK_OPTIONS.find((opt) => opt.type === element.type) ?? WATERMARK_OPTIONS[0];
  const isValid =
    (element.type !== WatermarkElementTypes.custom || !!element.settings.text.value.trim()) &&
    (element.type !== WatermarkElementTypes.image || !!element.settings.image.url);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const content = contentRef.current;
    if (!wrapper || !content) return;

    const targetHeight = content.scrollHeight;

    if (initiallyEditing && wrapper.style.height === '') {
      wrapper.style.height = '0px';
      void wrapper.offsetHeight;
    }

    const frame = requestAnimationFrame(() => {
      if (wrapperRef.current) {
        wrapperRef.current.style.height = `${targetHeight}px`;
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [isEditing, element, initiallyEditing]);

  const handleStartEdit = () => {
    editStartRef.current = element;
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (isNewRef.current) {
      if (wrapperRef.current) {
        isClosing.current = true;
        wrapperRef.current.style.height = '0px';
      } else {
        onRemove?.();
      }
    } else {
      onChange(editStartRef.current);
      setIsDirty(false);
      setIsEditing(false);
    }
  };

  const handleSave = async () => {
    await onSave();
    setIsEditing(false);
    isNewRef.current = false;
  };

  const handleDelete = () => {
    onDelete?.();
  };

  const handleTypeChange = (opt: typeof currentType) => {
    const newSettings = getDefaultSettings(opt.type);
    onChange({ ...element, type: opt.type as WatermarkElementTypes, settings: newSettings });
    setIsDirty(true);
  };

  const handleSettingsChange = (settings: IWatermarkElement['settings']) => {
    onChange({ ...element, settings });
    setIsDirty(true);
  };

  return (
    <div
      ref={wrapperRef}
      className={`${styles['watermark-card']} GlobalCont1`}
      onTransitionEnd={(e) => {
        if (e.propertyName === 'height' && isClosing.current && wrapperRef.current?.style.height === '0px') {
          onRemove?.();
        }
      }}
    >
      <div ref={contentRef}>
        {!isEditing ? (
          <Container className="flex ai-center jc-space-between padding-16">
            <Container className="flex ai-center gap-10">
              <Icon name={currentType.icon} size={16} />
              <Text className="P2">{currentType.label}</Text>
            </Container>
            <Button
              width="32px"
              height="32px"
              borderRadius="100px"
              backgroundColor="var(--global-cont-2)"
              hoverBackgroundColor="var(--global-cont-3)"
              className="padding-8"
              onClick={handleStartEdit}
            >
              <Icon name={ICONS.EDIT} size={16} />
            </Button>
          </Container>
        ) : (
          <Container className="flex-column padding-20 gap-16" loading={isLoading} opacity={isLoading ? '0.6' : '1'}>
            <WatermarkTypeSelector value={currentType} onChange={handleTypeChange} />

            <Container className="flex-column">
              <SwitchCase input={element.type}>
                <Case value={WatermarkElementTypes.email}>
                  <AddWatermarkEmailBlock settings={element.settings} onSettingsChange={handleSettingsChange} />
                </Case>
                <Case value={[WatermarkElementTypes.date, WatermarkElementTypes.time, WatermarkElementTypes.datetime]}>
                  <AddWatermarkDateBlock settings={element.settings} onSettingsChange={handleSettingsChange} />
                </Case>
                <Case value={WatermarkElementTypes.image}>
                  <AddWatermarkImageBlock settings={element.settings} onSettingsChange={handleSettingsChange} />
                </Case>
                <Case value={WatermarkElementTypes.custom}>
                  <AddWatermarkCustomBlock settings={element.settings} onSettingsChange={handleSettingsChange} />
                </Case>
              </SwitchCase>
            </Container>

            <Container className="flex jc-space-between gap-8">
              <Container>
                {canDelete && (
                  <Button
                    designSize="small"
                    design="LocalBttnSec"
                    className="radius-full square-32 flex-column ai-center jc-center"
                    onClick={handleDelete}
                    disabled={isLoading}
                  >
                    <Icon name={ICONS.DELETE_TRASH_BIN} size={12} />
                  </Button>
                )}
              </Container>
              <Container className="flex ai-center jc-flex-end gap-8">
                {canCancel && (
                  <Button design="LocalBttnSec" designSize="small" onClick={handleCancel} disabled={isLoading}>
                    <Text className="L1">Cancel</Text>
                  </Button>
                )}
                <Button
                  backgroundColor={getThemeColorCSSVariable()}
                  hoverBackgroundColor={getThemeColorsCssMixVariable()}
                  designSize="small"
                  disabled={(!isDirty && !isNewRef.current) || !isValid || isLoading}
                  disabledOpacity={0.6}
                  onClick={handleSave}
                >
                  <Text className="L1" color={getThemeColorContrastCSSVariable()}>
                    Save
                  </Text>
                </Button>
              </Container>
            </Container>
          </Container>
        )}
      </div>
    </div>
  );
};

export default AddWatermarkElementForm;
